import React, { useState } from 'react'
import Modal from '@components/base/Modal'
import { Button } from '@components/inputs/Button'
import ForummIcon from '@public/images/forummIcon.svg'
import BlackBaudIcon from '@public/images/BlackBaudIcon.svg'
import { useQuery } from '@apollo/client'
import { GET_BLACKBAUD_CONSTITUENT_OPTIONS } from '@graphql/events/getBlackBaudConstituentOptions'
import LoadingSpinner from '@components/base/LoadingSpinner'
import Box from '@components/base/Box'

interface Option {
  fullName: string
  email: string
  title?: string
  contact_id?: string
  postalAddress?: string
  dietaryRequirements?: string
  summary_note?: any
}

interface ConstituentPickerProps {
  show: boolean
  setShow: (show: boolean) => void
  name: string
  email: string
  fullAttendee: any
  onMatch: (
    cntData: { email: string; cId?: string },
    attendeeEmail: string
  ) => void
  options: Option[]
}

const ConstituentPicker: React.FC<ConstituentPickerProps> = ({
  show,
  setShow,
  name,
  email,
  onMatch,
  options,
  fullAttendee,
}) => {
  const [remoteOptions, setRemoteOptions] = useState<Option[]>(options)
  const [hasConstitutentsToMatch, setHasConstitutentsToMatch] =
    useState<boolean>(false)

  const metadata =
    typeof fullAttendee?.metadata === 'object' &&
    fullAttendee.metadata !== null &&
    !Array.isArray(fullAttendee.metadata)
      ? fullAttendee.metadata
      : {}

  const { loading } = useQuery(GET_BLACKBAUD_CONSTITUENT_OPTIONS, {
    variables: { email },
    onError: (err) => console.log({ err }),
    onCompleted: (data) => {
      setRemoteOptions((state) => {
        const payload = [
          ...state,
          ...data.getConstituentOptions
            .filter((c) => !!c?.email && !!c?.name && !!c?.id)
            .map((c) => ({
              fullName: c.name!,
              email: c.email!,
              contact_id: c.id!,
            })),
        ]
        // console.log({ payload })
        setHasConstitutentsToMatch(payload.length > 0)
        return payload
      })
    },
  })

  if (loading)
    return (
      <Box className="w-full flex justify-center">
        <LoadingSpinner size="small" />
      </Box>
    )

  return (
    <Modal show={show} setShow={setShow} title={name || 'Forumm User'}>
      <div className="p-4">
        {/* Selected Attendee Table */}
        <h3 className="font-base mb-4 mt-4">
          <ForummIcon
            className="float-left mr-1"
            viewBox="0 0 43 46"
            preserveAspectRatio="xMidYMid meet"
            height="24px"
            width="24px"
          />
          This is your local Forumm attendee.
        </h3>
        <div className="overflow-x-auto">
          <table className="table-auto w-full border-collapse">
            <thead>
              <tr className="bg-gray-200">
                <th className="px-4 py-2 text-left text-lg">Email</th>
                <th className="px-4 py-2 text-left text-lg">Full Name</th>
                {Object.keys(metadata).map((key, index) => (
                  <th
                    key={`${key}-${index}`}
                    className="px-4 py-2 text-left text-lg"
                  >
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="h-20 border-b border-slate-200">
                <td className="px-4 py-2 text-base">{fullAttendee?.email}</td>
                <td className="px-4 py-2 text-base">
                  {fullAttendee?.fullName}
                </td>
                {Object.values(metadata).map((value, idx) => (
                  <td key={idx} className="px-4 py-2 text-base">
                    {value as string}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Conditional Table or No Matches Message */}
        {hasConstitutentsToMatch && (
          <h3 className="font-base mb-4 mt-10">
            <BlackBaudIcon
              className="float-left mr-1"
              viewBox="0 0 58 58"
              preserveAspectRatio="xMidYMid meet"
              height="24px"
              width="24px"
            />
            Please match this participant to a Blackbaud constituent:
          </h3>
        )}
        {hasConstitutentsToMatch ? (
          <div className="overflow-x-auto">
            <table className="table-auto w-full border-collapse">
              <thead>
                <tr className="bg-gray-200">
                  <th className="px-4 py-2 text-left text-lg">Constituent</th>
                  <th className="px-4 py-2 text-left text-lg">Email</th>
                  <th className="px-4 py-2 text-left text-lg">Full Name</th>
                  <th className="px-4 py-2 text-left text-lg">Title</th>

                  <th className="px-4 py-2 text-left text-lg">
                    Postal Address
                  </th>
                  <th className="px-4 py-2 text-left text-lg">
                    Dietary Requirements
                  </th>
                  <th className="px-4 py-2 text-left text-lg">
                    Participant Data
                  </th>
                  <th className="px-4 py-2 text-left text-lg">
                    Match to Participant
                  </th>
                </tr>
              </thead>
              <tbody>
                {remoteOptions.map((option) => (
                  <tr
                    key={option.email}
                    className="h-20 border-b border-slate-200"
                  >
                    <td className="px-4 py-2 text-base">
                      {option?.contact_id ?? '-'}
                    </td>
                    <td className="px-4 py-2 text-base">{option.email}</td>
                    <td className="px-4 py-2 text-base">{option.fullName}</td>
                    <td className="px-4 py-2 text-base">
                      {option?.title ?? '-'}
                    </td>

                    <td className="px-4 py-2 text-base">
                      {option?.postalAddress ?? '-'}
                    </td>
                    <td className="px-4 py-2 text-base">
                      {option?.dietaryRequirements ?? '-'}
                    </td>
                    <td className="px-4 py-2 text-xs">
                      {option.summary_note ? (
                        (() => {
                          try {
                            const parsedData = JSON.parse(option.summary_note)
                            return (
                              <div className="text-xs">
                                {Object.entries(parsedData).map(
                                  ([key, value]) => (
                                    <div key={key}>
                                      <strong>{key}:</strong>{' '}
                                      {typeof value === 'object' ? (
                                        <pre className="whitespace-pre-wrap text-xs">
                                          {JSON.stringify(value, null, 2)}
                                        </pre>
                                      ) : (
                                        String(value)
                                      )}
                                    </div>
                                  )
                                )}
                              </div>
                            )
                          } catch (error) {
                            return <span className="text-red-500">-</span>
                          }
                        })()
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      <Button
                        title="Match"
                        type="blackbaud"
                        size="small"
                        onClick={() =>
                          onMatch(
                            { email: option.email, cId: option.contact_id },
                            email
                          )
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-center text-gray-500 mt-16 mb-10">
            There are no constituents that potentially match the email.
          </p>
        )}

        {/* Buttons Section */}
        <div className="flex justify-end mt-4">
          {/*<Button
            title="Go Back"
            type="primary"
            size="small"
            onClick={() => setShow(false)}
          />*/}
          <Button
            title="Save as new Constituents"
            type="blackbaud"
            size="small"
            className="ml-2"
            onClick={() => onMatch({ email: 'new' }, email)}
          />
        </div>
      </div>
    </Modal>
  )
}

export default ConstituentPicker
